import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { categories } from "@/mocks/categories";
import { movies } from "@/mocks/movies";
import usePageMeta from "@/hooks/usePageMeta";
import DetailActions from "./components/DetailActions";

export default function DetailPage() {
  const { id = "" } = useParams();
  const movie = movies.find((item) => item.id === id);

  usePageMeta(
    movie ? `${movie.title} - 日韩黄色电影详情与简介` : "影片不存在或已下架 - 日韩黄色电影",
    movie
      ? `${movie.title}（${movie.year}，${movie.region}）剧情简介：${movie.synopsis.slice(0, 90)}`
      : "该影片条目不存在或已下架，请返回首页浏览其他日韩影视作品。",
    movie
      ? `${movie.title},${movie.titleEn},${movie.director},${movie.genres.join(",")},日韩黄色电影`
      : "日韩黄色电影",
  );

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((item) => item.id !== movie.id)
      .map((item) => ({
        movie: item,
        score: item.genres.filter((genre) => movie.genres.includes(genre)).length,
      }))
      .sort((a, b) => b.score - a.score || b.movie.rating - a.movie.rating)
      .slice(0, 4)
      .map((entry) => entry.movie);
  }, [movie]);

  if (!movie) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 md:py-32 bg-background-50">
          <div className="w-full max-w-[560px] mx-auto text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center text-2xl text-foreground-500">
              <i className="ri-film-line" />
            </span>
            <h1 className="mt-6 font-heading text-2xl text-foreground-950">影片不存在或已下架</h1>
            <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
              该条目可能已被移除，或链接地址有误。你可以返回首页继续浏览其他作品。
            </p>
            <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/"
                className="h-11 px-6 rounded-md bg-primary-500 text-foreground-950 text-sm inline-flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
              <Link
                to="/category/popular"
                className="h-11 px-6 rounded-md border border-background-400/60 text-foreground-800 text-sm inline-flex items-center justify-center cursor-pointer hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                浏览热门影片
              </Link>
            </div>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const primaryCategory =
    categories.find((category) => movie.collections.includes(category.type)) ?? categories[2];

  return (
    <SiteLayout>
      <div className="relative w-full overflow-hidden bg-background-100 border-b border-background-300/60">
        <div className="absolute inset-0 opacity-20">
          <img
            src={movie.cover}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-top blur-2xl scale-110"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/85 to-background-50" />

        <div className="relative z-10 w-full px-4 md:px-6 py-8 md:py-12">
          <div className="w-full max-w-[1400px] mx-auto">
            <nav className="flex flex-wrap items-center gap-1.5 text-[11px] md:text-xs text-foreground-400">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <Link
                to={`/category/${primaryCategory.type}`}
                className="hover:text-accent-500 transition-colors cursor-pointer"
              >
                {primaryCategory.name}
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-700">{movie.title}</span>
            </nav>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-7 lg:gap-10">
              <div className="w-full max-w-[280px] mx-auto lg:mx-0 shrink-0">
                <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-300/70">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 影片封面`}
                    title={`${movie.title} - 日韩黄色电影详情`}
                    className="w-full h-full object-cover object-top"
                  />
                  <span className="absolute top-3 left-3 px-2 py-1 rounded-md bg-background-50/85 backdrop-blur-sm text-[10px] text-foreground-800">
                    {movie.lengthType}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <div className="rounded-md bg-background-100 border border-background-300/60 px-3 py-2.5">
                    <p className="text-[10px] text-foreground-500">站内评分</p>
                    <p className="mt-1 font-heading text-lg text-accent-500 flex items-center gap-1">
                      {movie.rating.toFixed(1)}
                      <i className="ri-star-fill text-[10px]" />
                    </p>
                  </div>
                  <div className="rounded-md bg-background-100 border border-background-300/60 px-3 py-2.5">
                    <p className="text-[10px] text-foreground-500">片长</p>
                    <p className="mt-1 font-heading text-lg text-foreground-900">
                      {movie.duration}
                      <span className="ml-1 text-[11px] text-foreground-500">分钟</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2 py-1 rounded-md bg-primary-500/20 text-primary-700 text-[11px]">
                    {movie.region}
                  </span>
                  <span className="px-2 py-1 rounded-md bg-background-200/80 text-foreground-600 text-[11px]">
                    {movie.year}
                  </span>
                  {movie.genres.map((genre) => (
                    <span
                      key={genre}
                      className="px-2 py-1 rounded-md bg-accent-500/10 text-accent-500 text-[11px]"
                    >
                      {genre}
                    </span>
                  ))}
                </div>

                <h1 className="mt-4 font-heading text-3xl md:text-5xl leading-tight text-foreground-950">
                  {movie.title}
                </h1>
                <p className="mt-2 font-label text-xs md:text-sm tracking-[0.2em] uppercase text-foreground-400">
                  {movie.titleEn}
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-foreground-500 shrink-0 w-12">导演</span>
                    <span className="text-foreground-800">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-foreground-500 shrink-0 w-12">主演</span>
                    <span className="text-foreground-800">{movie.cast.join(" / ")}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-foreground-500 shrink-0 w-12">年份</span>
                    <span className="text-foreground-800">{movie.year} 年</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <span className="text-foreground-500 shrink-0 w-12">收录</span>
                    <span className="text-foreground-800">{movie.publishDate}</span>
                  </div>
                </div>

                <div className="mt-7 max-w-[620px]">
                  <DetailActions title={movie.title} />
                  <p className="mt-3 text-[11px] leading-relaxed text-foreground-400">
                    收藏与分享仅为界面演示，不会保存任何用户数据。本站不提供播放与下载。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="w-full max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 lg:gap-12">
          <article className="min-w-0">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-primary-500" />
              剧情简介
            </h2>
            <p className="mt-4 text-sm md:text-[15px] leading-[1.95] text-foreground-600">
              {movie.synopsis}
            </p>

            <h2 className="mt-10 font-heading text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500" />
              创作背景
            </h2>
            <p className="mt-4 text-sm md:text-[15px] leading-[1.95] text-foreground-600">
              本片由 {movie.director} 执导，{movie.cast[0]} 与 {movie.cast[1]} 主演，
              以{movie.genres.join("、")}为主要基调，全长 {movie.duration} 分钟。
              影片在光线与声音的处理上刻意克制，把叙事的重心交给人物之间的沉默与距离，
              是{movie.region}同期作品中较具代表性的一部。
            </p>

            <div className="mt-8 rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-5">
              <p className="text-[11px] leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1.5" />
                本条目为演示用虚构内容，影片名称、导演、演员与剧情均非真实作品；本站不提供任何在线播放、下载或资源分发服务。
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="text-xs text-foreground-500">所属分类</span>
              <Link
                to={`/category/${primaryCategory.type}`}
                className="px-3 py-1.5 rounded-md border border-background-300/70 text-xs text-foreground-700 hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className={primaryCategory.icon} />
                {primaryCategory.name}
              </Link>
              <Link
                to="/category/themes"
                className="px-3 py-1.5 rounded-md border border-background-300/70 text-xs text-foreground-700 hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
              >
                <i className="ri-bookmark-3-line" />
                多元题材
              </Link>
            </div>
          </article>

          <aside className="min-w-0">
            <div className="rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-5 lg:sticky lg:top-24">
              <h3 className="font-heading text-base text-foreground-950">影片信息</h3>
              <dl className="mt-4 space-y-3 text-xs">
                {[
                  { label: "原名", value: movie.titleEn },
                  { label: "地区", value: movie.region },
                  { label: "年份", value: `${movie.year}` },
                  { label: "片长", value: `${movie.duration} 分钟` },
                  { label: "类型", value: movie.lengthType },
                  { label: "导演", value: movie.director },
                  { label: "题材", value: movie.genres.join(" / ") },
                ].map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-3">
                    <dt className="text-foreground-500 shrink-0">{row.label}</dt>
                    <dd className="text-foreground-700 text-right">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>
      </div>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100 border-t border-background-300/60">
        <div className="w-full max-w-[1400px] mx-auto">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="font-label text-[11px] tracking-[0.26em] uppercase text-accent-500">
                You May Also Like
              </p>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl text-foreground-950">
                相关影片推荐
              </h2>
            </div>
            <Link
              to="/category/popular"
              className="text-xs md:text-sm text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap hidden sm:flex items-center gap-1.5"
            >
              更多影片
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="mt-6 flex gap-4 overflow-x-auto no-scrollbar pb-2 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-5 sm:overflow-visible">
            {related.map((item) => (
              <div key={item.id} className="min-w-[150px] sm:min-w-0">
                <MovieCard movie={item} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}